import type { MultiSelectOption } from '@/components/MultiSelectFilter'
import {
  hasActiveAmountFilter,
  matchesNumericFilter,
  type TableAmountFilterOp,
} from '@/components/tableFilterUtils'

export type CatalogAmountFilterOp = TableAmountFilterOp

export interface CatalogTableFilters {
  bk_sku: string[]
  wh_sku: string[]
  display_name: string[]
  units_per_case_op: CatalogAmountFilterOp
  units_per_case: string
  cases_left_op: CatalogAmountFilterOp
  cases_left: string
  avg_cost_op: CatalogAmountFilterOp
  avg_cost: string
}

export const EMPTY_CATALOG_TABLE_FILTERS: CatalogTableFilters = {
  bk_sku: [],
  wh_sku: [],
  display_name: [],
  units_per_case_op: 'eq',
  units_per_case: '',
  cases_left_op: 'eq',
  cases_left: '',
  avg_cost_op: 'eq',
  avg_cost: '',
}

export type CatalogFilterRow = {
  bk_sku: string
  wh_sku?: string | null
  display_name: string
  units_per_case?: number | null
  cases_left?: number | null
  avg_cost?: number | null
}

function matchesMultiSelectFilter(
  value: string | null | undefined,
  selected: string[]
): boolean {
  if (selected.length === 0) return true
  const normalized = (value ?? '').trim()
  if (!normalized) return false
  return selected.includes(normalized)
}

function distinctNonEmptyOptions(
  rows: CatalogFilterRow[],
  getter: (row: CatalogFilterRow) => string | null | undefined
): MultiSelectOption[] {
  const values = new Set<string>()
  for (const row of rows) {
    const value = (getter(row) ?? '').trim()
    if (!value) continue
    values.add(value)
  }
  return Array.from(values)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map((id) => ({ id, label: id }))
}

export function buildCatalogFilterOptions(rows: CatalogFilterRow[]): {
  bk_sku: MultiSelectOption[]
  wh_sku: MultiSelectOption[]
  display_name: MultiSelectOption[]
} {
  return {
    bk_sku: distinctNonEmptyOptions(rows, (row) => row.bk_sku),
    wh_sku: distinctNonEmptyOptions(rows, (row) => row.wh_sku),
    display_name: distinctNonEmptyOptions(rows, (row) => row.display_name),
  }
}

export function hasActiveCatalogTableFilters(filters: CatalogTableFilters): boolean {
  return (
    filters.bk_sku.length > 0 ||
    filters.wh_sku.length > 0 ||
    filters.display_name.length > 0 ||
    hasActiveAmountFilter(filters.units_per_case, filters.units_per_case_op) ||
    hasActiveAmountFilter(filters.cases_left, filters.cases_left_op) ||
    hasActiveAmountFilter(filters.avg_cost, filters.avg_cost_op)
  )
}

export function applyCatalogFilters<T extends CatalogFilterRow>(
  rows: T[],
  filters: CatalogTableFilters
): T[] {
  return rows.filter((row) => {
    if (!matchesMultiSelectFilter(row.bk_sku, filters.bk_sku)) return false
    if (!matchesMultiSelectFilter(row.wh_sku, filters.wh_sku)) return false
    if (!matchesMultiSelectFilter(row.display_name, filters.display_name)) return false
    if (
      !matchesNumericFilter(row.units_per_case ?? null, filters.units_per_case, filters.units_per_case_op)
    ) {
      return false
    }
    if (
      !matchesNumericFilter(row.cases_left ?? null, filters.cases_left, filters.cases_left_op)
    ) {
      return false
    }
    if (!matchesNumericFilter(row.avg_cost ?? null, filters.avg_cost, filters.avg_cost_op)) {
      return false
    }
    return true
  })
}
